import React, { useEffect, useState } from "react";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
} from "@nextui-org/react";
import { FaChevronDown } from "react-icons/fa";
import { capitalize, UpdateOneData } from "../../../controllers/APIController";
import {
  getMetricsAssignedToUser,
  getUserRoleFromOrg,
  timeAgo,
} from "../../../utils/helper";
import { AVAILABLE_USER_ROLES } from "../../../constant/FormBuilder/FormBuilderConstant";
import { StandardDeleteButton } from "../../../components/StandardButtons";

// Use centralized roles constant
const availableRoles = AVAILABLE_USER_ROLES;

// Organization field mappings for role management
const organizationFieldMap = {
  Admin: "admin_list",
  Authenticated: "",
  Reviewer: "reviewer_list",
  Approver: "approver_list",
};

/**
 * DrawerBody Component
 * Body section of the user drawer showing user details and role management
 * Allows administrators to view and modify user roles and permissions
 * @param {Object} props - Component props
 * @param {Object} props.User - User object containing user information
 * @param {string} props.OrgInfo - Organization ID for the current context
 * @returns {JSX.Element} User drawer body with role management
 */
const DrawerBody = ({ User, OrgInfo }) => {
  // Component state
  const [userRole, setUserRole] = useState("");
  const [initialRole, setInitialRole] = useState("");
  const [validationMessage, setValidationMessage] = useState("");

  // Extract and format user name
  const extractedName = capitalize(User?.FirstName || User?.email?.split("@")[0]);

  // Format user creation date
  const creationDate = new Date(User?.createdAt || null);
  const formattedDate = creationDate.toLocaleDateString("en-GB", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // Calculate time since last login
  const lastLoginAt = User?.lastLoginAt
    ? timeAgo(+User?.lastLoginAt)
    : "long time ago";

  /**
   * Validate role change operation
   * Checks if user has assigned metrics before allowing role change
   * @returns {Object} Validation result with isValid flag and error message
   */
  const validateRoleChange = async () => {
    setValidationMessage("");
    
    // Admin and Authenticated roles don't require validation
    if (initialRole === "Admin" || initialRole === "Authenticated") {
      return { isValid: true, err: {} };
    }

    // Check if user has assigned metrics for role-specific validation
    if (initialRole === "Reviewer") {
      const response = await getMetricsAssignedToUser(
        User?.id,
        "ReviewerToMetric"
      );
      if (response?.length === 0) {
        return { isValid: true, err: {} };
      }
    } else if (initialRole === "Approver") {
      const response = await getMetricsAssignedToUser(
        User?.id,
        "ApproverToMetric"
      );
      if (response?.length === 0) {
        return { isValid: true, err: {} };
      }
    }

    return {
      isValid: false,
      err: `You need to re-assign ${initialRole} List before changing role`,
    };
  };

  /**
   * Handle profile update with role change
   * Updates user role in the organization and handles role-specific assignments
   */
  const handleProfileUpdate = async () => {
    if (userRole === initialRole) return;
    
    const validationResult = await validateRoleChange();
    if (!validationResult?.isValid) {
      setValidationMessage(validationResult?.err);
      setUserRole(initialRole);
      return;
    }

    let connectedOrg = null;
    let disconnectedOrg = null;

    // Connect user to new role organization
    if (userRole !== "Authenticated") {
      const field = organizationFieldMap[userRole];
      connectedOrg = {
        [field]: {
          connect: [User.id],
        },
      };
    }

    // Disconnect user from previous role organization
    if (initialRole !== "Authenticated") {
      const field = organizationFieldMap[initialRole];
      disconnectedOrg = {
        [field]: {
          disconnect: [User.id],
        },
      };
    }

    // Prepare update data
    const updateData = {
      ...connectedOrg,
      ...disconnectedOrg,
    };

    try {
      await UpdateOneData("organizations", OrgInfo, updateData);
      setInitialRole(userRole);
    } catch (error) {
      console.error("Error updating user role:", error);
      setValidationMessage("Failed to update user role. Please try again.");
    }
  };

  /**
   * Fetch user role on component mount
   * Loads the current user's role from the organization
   */
  useEffect(() => {
    getUserRoleFromOrg(OrgInfo, User?.id)
      .then((data) => {
        setInitialRole(data);
        setUserRole(data);
      })
      .catch((error) => {
        console.error("Error fetching user role:", error);
      });
  }, [User, OrgInfo]);

  return (
    <div className="flex flex-col items-start space-y-2">
      {/* User Name */}
      <div className="flex items-center w-full font-medium h-8 text-sm">
        <span className="w-[25%]">Name :</span>
        <span className="text-warmGray-500 font-normal">{extractedName}</span>
      </div>
      
      {/* User Role Dropdown */}
      <div className="flex w-full font-medium text-sm">
        <span className="flex items-center w-[25%]">Role :</span>
        <Dropdown>
          <DropdownTrigger className="w-[50%]">
            <Button variant="faded" className="w-[50%] h-8" size="sm">
              <div className="w-full flex justify-between">
                <div className="flex justify-start w-full items-start">
                  {userRole}
                </div>
                <div className="flex justify-end w-full items-end">
                  <FaChevronDown />
                </div>
              </div>
            </Button>
          </DropdownTrigger>
          <DropdownMenu
            aria-label="User role selection"
            className="w-52 custom-dropdown-menu"
            selectionMode="single"
            selectedKeys={new Set([userRole])}
            onSelectionChange={(event) => {
              setUserRole(Array.from(event).join(", ").replaceAll("_", " "));
            }}
          >
            {availableRoles?.map((role) => (
              <DropdownItem key={role.name} value={role.name}>
                {role.name}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </Dropdown>
      </div>
      
      {/* Validation Error Message */}
      {validationMessage !== "" && (
        <div className="flex items-center w-full">
          <span className="w-[25%]"></span>
          <span className="text-[#f76497] text-xs">{validationMessage}</span>
        </div>
      )}

      {/* User Since Date */}
      <div className="flex items-center w-full font-medium h-8 text-sm">
        <span className="w-[25%]">User Since :</span>
        <span className="text-warmGray-500 font-normal">
          {formattedDate || "12 August, 2023"}
        </span>
      </div>
      
      {/* Last Login Time */}
      <div className="flex items-center w-full font-medium h-8 text-sm">
        <span className="w-[25%]">Last Login :</span>
        <span className="text-warmGray-500 font-normal">{lastLoginAt}</span>
      </div>
      
      {/* Update Profile Button */}
      <div className="flex items-center justify-end w-full pt-4 pr-4">
        <StandardDeleteButton size="sm" onClick={handleProfileUpdate} aria-label="Update user profile">
          Update Profile
        </StandardDeleteButton>
      </div>
    </div>
  );
};

export default DrawerBody;
